import { Users } from "lucide-react";
import { SOCCER_CODES } from "../lib/sports";
import { TeamLogo } from "./TeamLogo";


// Final-margin std per sport — converts the market spread into an implied win probability.
const SIGMA = { NBA: 13, WNBA: 12, NFL: 13.5, NHL: 2.2, MLB: 4.5 };
const SOCCER = new Set(SOCCER_CODES);
const sigmaFor = (sport) => SIGMA[sport] || (SOCCER.has(sport) ? 1.35 : 12);
const normCdf = (x) => 1 / (1 + Math.exp(-1.702 * x));

// Home win % implied by the real sportsbook spread (home_spread negative => home favored).
export const gameLeanHomePct = (sport, homeSpread) => {
  const s = sigmaFor(sport);
  const expHomeMargin = -(homeSpread || 0);
  return Math.round(normCdf(expHomeMargin / s) * 100);
};

const impliedProb = (odds) => (odds < 0 ? -odds / (-odds + 100) : 100 / (odds + 100));

// % the market leans toward OVER, vig removed, from the two prices.
export const propLeanOverPct = (overOdds, underOdds) => {
  const o = impliedProb(overOdds ?? -110);
  const u = impliedProb(underOdds ?? -110);
  const tot = o + u || 1;
  return Math.round((o / tot) * 100);
};

// "Sharp vs Public" — flag props where the AI/model lean disagrees with where the market leans.
// Returns null unless it's a clear disagreement (market leans >= 55% the OTHER way).
export const getValueSpot = (prop) => {
  if (!prop?.lean) return null;
  const overPct = propLeanOverPct(prop.over_odds, prop.under_odds);
  const marketSide = overPct >= 50 ? "OVER" : "UNDER";
  const marketPct = Math.max(overPct, 100 - overPct);
  if (marketSide !== prop.lean && marketPct >= 55) {
    return { aiSide: prop.lean, marketSide, marketPct };
  }
  return null;
};

// "Consensus" — flag props where the AI pick and the market lean AGREE (same side).
// These are higher-confidence bets. "strong" when the market is convicted and the L10 hit-rate backs it.
// A sportsbook "agrees" when the AI projection beats its line on the AI side AND its price leans that way.
export const getBooksAgree = (prop) => {
  const books = Object.values(prop?.book_lines || {});
  if (!prop?.lean || prop.projection == null || !books.length) return null;
  const agree = books.filter((b) => {
    const beats = prop.lean === "OVER" ? prop.projection > b.line : prop.projection < b.line;
    const overPct = propLeanOverPct(b.over, b.under);
    return beats && (prop.lean === "OVER" ? overPct : 100 - overPct) >= 50;
  }).length;
  return { agree, total: books.length };
};

// Consensus = AI projection + a majority of sportsbooks on the same side (mirrors backend).
export const getConsensus = (prop) => {
  if (!prop?.lean) return null;
  const ba = getBooksAgree(prop);
  let marketPct;
  if (ba) {
    marketPct = Math.round((ba.agree / ba.total) * 100);
    if (marketPct <= 50) return null;
  } else {
    const overPct = propLeanOverPct(prop.over_odds, prop.under_odds);
    marketPct = prop.lean === "OVER" ? overPct : 100 - overPct;
    if (marketPct < 52) return null;
  }
  const strong = marketPct >= 75 && (prop.hit_rate ?? 0) >= 60 && (!ba || ba.total >= 3);
  return { side: prop.lean, marketPct, strong, booksAgree: ba?.agree, booksTotal: ba?.total };
};

export const GameLeanBar = ({ sport, spread, away, home }) => {
  const homePct = gameLeanHomePct(sport, spread);
  const awayPct = 100 - homePct;
  const homeLeads = homePct >= awayPct;
  const leanTeam = homeLeads ? home : away;
  const leanPct = Math.max(homePct, awayPct);
  return (
    <div data-testid="market-lean" className="w-full">
      <div className="flex items-center justify-between mb-1">
        <span className="flex items-center gap-1 text-[10px] font-data uppercase tracking-wide text-slate-400">
          <Users className="h-3 w-3" /> Market Lean
        </span>
        <span className="inline-flex items-center gap-1 text-[10px] font-data font-bold text-white">
          <TeamLogo sport={leanTeam.sport} abbr={leanTeam.abbr} color={leanTeam.color} size="xs" /> {leanPct}%
        </span>
      </div>
      <div className="flex h-2 rounded-full overflow-hidden bg-[#233248]">
        <div style={{ width: `${awayPct}%`, background: away.color }} title={`${away.name || away.abbr} ${awayPct}%`} />
        <div style={{ width: `${homePct}%`, background: home.color }} title={`${home.name || home.abbr} ${homePct}%`} />
      </div>
      <div className="flex items-center justify-between mt-1 font-data text-[10px] text-slate-400">
        <span className="inline-flex items-center gap-1"><TeamLogo sport={away.sport} abbr={away.abbr} color={away.color} size="xs" /> {awayPct}%</span>
        <span className="inline-flex items-center gap-1">{homePct}% <TeamLogo sport={home.sport} abbr={home.abbr} color={home.color} size="xs" /></span>
      </div>
    </div>
  );
};

export const PropLeanBar = ({ overOdds, underOdds }) => {
  const overPct = propLeanOverPct(overOdds, underOdds);
  const underPct = 100 - overPct;
  const overLeads = overPct >= underPct;
  const color = overLeads ? "#10B981" : "#EF4444";
  return (
    <div data-testid="prop-market-lean" className="mt-2">
      <div className="flex items-center justify-between mb-1">
        <span className="flex items-center gap-1 text-[10px] font-data uppercase tracking-wide text-slate-400">
          <Users className="h-3 w-3" /> Market
        </span>
        <span className="text-[10px] font-data font-bold" style={{ color }}>
          {overLeads ? "OVER" : "UNDER"} {Math.max(overPct, underPct)}%
        </span>
      </div>
      <div className="flex h-1.5 rounded-full overflow-hidden bg-[#233248]">
        <div style={{ width: `${overPct}%`, background: "#10B981" }} title={`Over ${overPct}%`} />
        <div style={{ width: `${underPct}%`, background: "#EF4444" }} title={`Under ${underPct}%`} />
      </div>
    </div>
  );
};
